import React, { useState, useCallback } from 'react';
import Icon from './AppIcon';

const BASE_URL = 'https://www.octalchip.com';

/**
 * Builds share URLs for major social platforms.
 * @param {string} url - Full URL of the page to share
 * @param {string} title - Title of the content
 * @param {string} [text] - Optional description or quote
 */
/**
 * Builds a mailto: URL with a clear subject and readable body.
 * Body is fully encoded so newlines work across email clients.
 */
function getEmailShareUrl(url, title, text = '') {
  const subject = title ? `Article: ${title}` : 'Check out this article';
  const bodyLines = [
    "I thought you might find this article interesting:",
    "",
    text || title || "Here's a link to an article I wanted to share.",
    "",
    url,
  ];
  const body = bodyLines.join("\n");
  return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

function getShareUrls(url, title, text = '') {
  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);
  const titleAndUrl = `${title} ${url}`;

  return {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
    twitter: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
    whatsapp: `https://wa.me/?text=${encodeURIComponent(titleAndUrl)}`,
    telegram: `https://t.me/share/url?url=${encodedUrl}&text=${encodedTitle}`,
    pinterest: `https://pinterest.com/pin/create/button/?url=${encodedUrl}&description=${encodedTitle}`,
    bluesky: `https://bsky.app/intent/compose?text=${encodeURIComponent(titleAndUrl)}`,
    email: getEmailShareUrl(url, title, text),
  };
}

// Platforms with shareUrlKey open that platform’s share dialog. Instagram has custom handling (Web Share + app scheme).
const SHARE_PLATFORMS = [
  { key: 'facebook', name: 'Facebook', icon: 'Facebook', shareUrlKey: 'facebook' },
  { key: 'instagram', name: 'Instagram', icon: 'Instagram' },
  { key: 'linkedin', name: 'LinkedIn', icon: 'Linkedin', shareUrlKey: 'linkedin' },
  { key: 'twitter', name: 'Twitter / X', icon: 'Twitter', shareUrlKey: 'twitter' },
  { key: 'pinterest', name: 'Pinterest', icon: 'Pinterest', shareUrlKey: 'pinterest' },
  { key: 'bluesky', name: 'Bluesky', icon: 'Bluesky', shareUrlKey: 'bluesky' },
  { key: 'whatsapp', name: 'WhatsApp', icon: 'WhatsApp', shareUrlKey: 'whatsapp' },
  { key: 'telegram', name: 'Telegram', icon: 'Telegram', shareUrlKey: 'telegram' },
  { key: 'email', name: 'Email', icon: 'Mail', shareUrlKey: 'email' },
];

function BlogShare({ title = '', slug = '', shortDescription = '', className = '' }) {
  const [copied, setCopied] = useState(false);
  const fullUrl = `${BASE_URL}/blog/${slug}`;
  const shareUrls = getShareUrls(fullUrl, title, shortDescription);
  const shareTitle = title || 'Blog post';

  const handleCopyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers or non-HTTPS
      const input = document.createElement('input');
      input.value = fullUrl;
      input.setAttribute('readonly', '');
      input.style.position = 'absolute';
      input.style.left = '-9999px';
      input.style.opacity = '0';
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [fullUrl]);

  const handleShare = (shareUrlKey) => {
    const url = shareUrls[shareUrlKey];
    if (!url) return;
    // mailto: works best when opened in current context so the default mail client opens without a blank tab
    if (shareUrlKey === 'email') {
      window.location.href = url;
    } else {
      window.open(url, '_blank', 'noopener,noreferrer,width=600,height=500');
    }
  };

  // Instagram: share sheet first; else try opening app with link in message (instagram://sharesheet); else copy link.
  const handleInstagramShare = useCallback(() => {
    const titleAndUrl = `${shareTitle} ${fullUrl}`;
    const instagramShareUrl = `instagram://sharesheet?text=${encodeURIComponent(titleAndUrl)}`;

    if (typeof navigator !== 'undefined' && navigator.share) {
      navigator.share({ title: shareTitle, text: shortDescription || shareTitle, url: fullUrl })
        .catch((err) => {
          if (err.name !== 'AbortError') {
            handleCopyLink();
          }
        });
      return;
    }

    // No Web Share (e.g. desktop): try opening Instagram app with message pre-filled (works on mobile).
    window.location.href = instagramShareUrl;
    // If still here after a short delay (desktop or app not installed), copy link so they can paste in Instagram.
    setTimeout(handleCopyLink, 1500);
  }, [shareTitle, shortDescription, fullUrl, handleCopyLink]);

  const buttonClass =
    'flex h-9 w-9 items-center justify-center rounded-full bg-white text-gray-600 shadow-sm ring-1 ring-gray-200 transition-all hover:bg-[#00d690] hover:text-white hover:ring-[#00d690] focus:outline-none focus:ring-2 focus:ring-[#00d690] focus:ring-offset-2 sm:h-10 sm:w-10';

  return (
    <div
      className={`rounded-xl border border-gray-100 bg-gray-50/80 p-4 sm:p-5 md:p-6 ${className}`}
      role="region"
      aria-label="Share this article"
    >
      <div className="flex flex-wrap items-center gap-3 sm:gap-4">
        <span className="flex items-center gap-2 text-sm font-semibold text-gray-700 sm:text-base">
          <Icon name="Share2" size={18} className="text-[#00d690]" aria-hidden />
          Share this article
        </span>
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          {SHARE_PLATFORMS.map(({ key, name, icon, shareUrlKey }) =>
            shareUrlKey ? (
              <a
                key={key}
                href={shareUrls[shareUrlKey]}
                target={shareUrlKey === 'email' ? undefined : '_blank'}
                rel={shareUrlKey === 'email' ? undefined : 'noopener noreferrer'}
                onClick={(e) => {
                  e.preventDefault();
                  handleShare(shareUrlKey);
                }}
                className={buttonClass}
                title={shareUrlKey === 'email' ? 'Share by email' : `Share on ${name}`}
                aria-label={shareUrlKey === 'email' ? 'Share by email' : `Share on ${name}`}
              >
                <Icon name={icon} size={18} className="sm:w-5 sm:h-5" />
              </a>
            ) : (
              <button
                key={key}
                type="button"
                onClick={handleInstagramShare}
                className={buttonClass}
                title={`Share link in Instagram message`}
                aria-label={`Share link in Instagram message`}
              >
                <Icon name={icon} size={18} className="sm:w-5 sm:h-5" />
              </button>
            )
          )}
          <button
            type="button"
            onClick={handleCopyLink}
            className={buttonClass}
            title={copied ? 'Copied!' : 'Copy link'}
            aria-label={copied ? 'Link copied' : 'Copy link'}
          >
            {copied ? (
              <Icon name="Check" size={18} className="text-[#00d690] sm:w-5 sm:h-5" />
            ) : (
              <Icon name="Link" size={18} className="sm:w-5 sm:h-5" />
            )}
          </button>
        </div>
        {copied && (
          <span className="text-xs font-medium text-[#00d690] sm:text-sm" role="status">
            Link copied!
          </span>
        )}
      </div>
    </div>
  );
}

export default BlogShare;
